"use client";

import { AlertTriangle, ArrowRight, Check, ShieldCheck, X } from "lucide-react";
import { useMemo, useState } from "react";
import type { ParsedOrder, SoapSection } from "@/src/services/mockAiService";
import { runAuditorChecks, type AuditFinding, type AuditFlag } from "@/src/services/auditorChecks";
import { useLang } from "@/src/i18n";

type AuditorPanelProps = {
  soap: Record<SoapSection, string>;
  orders: ParsedOrder[];
  onFocusField: (field: SoapSection) => void;
  onSendToSoap: (action: { field: SoapSection; text: string; title: string; evidence: string }) => void;
  onDraftOrder: (order: ParsedOrder, source: { title: string; evidence: string }) => void;
};

// Panel chrome is bilingual; clinical finding text stays English (as the
// Heartbeat feed does), so a mixed TH/EN reviewer reads the same wording.
const CHROME = {
  en: {
    title: "Documentation check",
    subOpen: (n: number) => `${n} thing${n === 1 ? "" : "s"} to review before you finalise`,
    subClear: "This note is complete — nothing to review",
    clear: "No documentation gaps found for this note.",
    gap: "What is missing",
    rec: "Do this",
    grounded: "Grounded on",
    dismiss: "Dismiss",
    sev: { high: "High", medium: "Medium", low: "Low" },
  },
  th: {
    title: "ตรวจความครบถ้วนของเวชระเบียน",
    subOpen: (n: number) => `มี ${n} รายการที่ควรตรวจก่อนบันทึกฉบับสมบูรณ์`,
    subClear: "บันทึกนี้ครบถ้วน — ไม่มีรายการต้องตรวจ",
    clear: "ไม่พบข้อมูลที่ขาดหายในบันทึกนี้",
    gap: "สิ่งที่ขาดหาย",
    rec: "สิ่งที่ควรทำ",
    grounded: "อ้างอิงจาก",
    dismiss: "ซ่อน",
    sev: { high: "สูง", medium: "กลาง", low: "ต่ำ" },
  },
} as const;

const FLAG_LABEL: Record<AuditFlag, { en: string; th: string }> = {
  "missing-doc": { en: "Missing documentation", th: "เอกสาร/หลักฐานไม่ครบ" },
  overclaim: { en: "Over-coded", th: "ลงรหัสเกินหลักฐาน" },
  underclaim: { en: "Under-coded", th: "ลงรหัสไม่ครบ" },
  "insufficient-data": { en: "Insufficient data", th: "ข้อมูลไม่พอประเมิน" },
};

export function AuditorPanel({ soap, orders, onFocusField, onSendToSoap, onDraftOrder }: AuditorPanelProps) {
  const { lang } = useLang();
  const L = CHROME[lang === "th" ? "th" : "en"];
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const findings = useMemo(() => runAuditorChecks({ soap, orders }), [soap, orders]);
  const open = findings.filter((f) => !dismissed.has(f.id));

  function applyFix(f: AuditFinding) {
    if (!f.fix) return;
    if (f.fix.kind === "focus") onFocusField(f.fix.field);
    else if (f.fix.kind === "soap")
      onSendToSoap({ field: f.fix.field, text: f.fix.text, title: f.fix.title, evidence: f.fix.evidence });
    else if (f.fix.kind === "order") onDraftOrder(f.fix.order, { title: f.fix.title, evidence: f.fix.evidence });
  }

  return (
    <section className="auditor-panel" aria-label={L.title}>
      <div className="auditor-head">
        <span className="a-ic">
          <ShieldCheck size={17} />
        </span>
        <h3>{L.title}</h3>
        <span className={`auditor-count ${open.length ? "" : "clear"}`}>{open.length}</span>
      </div>
      <p className="auditor-sub">{open.length ? L.subOpen(open.length) : L.subClear}</p>

      {open.length === 0 ? (
        <div className="auditor-empty">
          <Check size={16} /> {L.clear}
        </div>
      ) : (
        open.map((f) => (
          <article className={`aud-finding sev-${f.severity}`} key={f.id}>
            <div className="aud-row1">
              <span className={`aud-sev ${f.severity}`}>{L.sev[f.severity]}</span>
              <span className="aud-flag">{FLAG_LABEL[f.flag][lang === "th" ? "th" : "en"]}</span>
              <button
                type="button"
                className="aud-dismiss"
                onClick={() => setDismissed((prev) => new Set(prev).add(f.id))}
                aria-label={L.dismiss}
                title={L.dismiss}
              >
                <X size={14} />
              </button>
            </div>
            <p className="aud-title">{f.title}</p>
            <p className="aud-detail">{f.detail}</p>
            <p className="aud-gap">
              <AlertTriangle size={12} /> <b>{L.gap}:</b> {f.gap}
            </p>
            <p className="aud-rec">
              <b>{L.rec}:</b> {f.recommendation}
            </p>
            <div className="aud-actions">
              {f.fix && (
                <button type="button" className="aud-fix" onClick={() => applyFix(f)}>
                  {f.fix.label} <ArrowRight size={13} />
                </button>
              )}
              <span className="aud-ground">
                {L.grounded}: <b>{f.grounding}</b>
              </span>
            </div>
          </article>
        ))
      )}
    </section>
  );
}
